/**
 * Utility functions for formatting time period display text for crosstabs
 * According to PRD: Crosstab Stat Testing - Time Period Display
 */

import type { TimePeriod } from "@/store/slices/crosstab-config-slice";
import { getTimePeriodDates } from "@/utils/time-period-utils";

/**
 * Get the quarter number and year from a date
 */
function getQuarterInfo(date: Date): { quarter: number; year: number } {
  const month = date.getMonth(); // 0-11
  const quarter = Math.floor(month / 3) + 1; // 1-4
  const year = date.getFullYear();
  return { quarter, year };
}

/**
 * Get the semester number and year from a date
 */
function getSemesterInfo(date: Date): { semester: number; year: number } {
  const month = date.getMonth(); // 0-11
  const semester = month < 6 ? 1 : 2;
  const year = date.getFullYear();
  return { semester, year };
}

/**
 * Format a date string (YYYY-MM-DD) to mm/dd/yyyy
 * Parses date as local date to avoid timezone conversion issues
 */
function formatDateToMMDDYYYY(dateString: string): string {
  // Parse as local date to prevent timezone shifts
  const [year, month, day] = dateString.split("-").map(Number);
  return `${String(month).padStart(2, "0")}/${String(day).padStart(2, "0")}/${year}`;
}

/**
 * Parse a date string (YYYY-MM-DD or MM/DD/YYYY) into a local Date object.
 */
function parseDateString(dateStr: string): Date | null {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [month, day, year] = dateStr.split("/").map(Number);
    return new Date(year, month - 1, day);
  }
  return null;
}

/**
 * Get the nominal representation for a time period
 * Returns the human-readable format like "Q4 2025" or "Semester 1 2025"
 *
 * For named periods, uses the resolved endDate when available — these dates
 * are resolved from the dataset availability cutoff by the handler hooks,
 * so they reflect the actual data range rather than today's date.
 */
export function getTimePeriodNominalRepresentation(
  timePeriod: TimePeriod,
  startDate?: string | null,
  endDate?: string | null,
  availabilityCutoff?: string | null,
): string | null {
  switch (timePeriod) {
    case "all_time":
      return null;

    case "latest_quarter":
    case "previous_quarter": {
      let resolvedEnd = endDate;
      if (!resolvedEnd) {
        const resolved = getTimePeriodDates(timePeriod, availabilityCutoff);
        resolvedEnd = resolved?.endDate ?? null;
      }
      const referenceDate = resolvedEnd ? parseDateString(resolvedEnd) : null;
      const { quarter, year } = getQuarterInfo(referenceDate ?? new Date());
      return `Q${quarter} ${year}`;
    }

    case "latest_semester":
    case "previous_semester": {
      let resolvedEnd = endDate;
      if (!resolvedEnd) {
        const resolved = getTimePeriodDates(timePeriod, availabilityCutoff);
        resolvedEnd = resolved?.endDate ?? null;
      }
      const referenceDate = resolvedEnd ? parseDateString(resolvedEnd) : null;
      const { semester, year } = getSemesterInfo(referenceDate ?? new Date());
      return `Semester ${semester} ${year}`;
    }

    case "custom_period": {
      if (startDate && endDate) {
        const formattedStart = formatDateToMMDDYYYY(startDate);
        const formattedEnd = formatDateToMMDDYYYY(endDate);
        return `${formattedStart} to ${formattedEnd}`;
      }
      return null;
    }

    default:
      return null;
  }
}

/**
 * Get the full time period display text for crosstab title
 * Format: "[Question Text] - [Selected Time Period]"
 */
export function getTimePeriodTitleSuffix(
  timePeriod: TimePeriod,
  startDate?: string | null,
  endDate?: string | null,
  availabilityCutoff?: string | null,
): string | null {
  const nominalRepresentation = getTimePeriodNominalRepresentation(
    timePeriod,
    startDate,
    endDate,
    availabilityCutoff,
  );

  return nominalRepresentation ? ` - ${nominalRepresentation}` : null;
}

/**
 * Get the time period footer text
 * Format: "Time Period: [Selected Time Period] - [Nominal Representation]"
 * Used when user has customized the title
 */
export function getTimePeriodFooterText(
  timePeriod: TimePeriod,
  startDate?: string | null,
  endDate?: string | null,
  availabilityCutoff?: string | null,
): string | null {
  const nominalRepresentation = getTimePeriodNominalRepresentation(
    timePeriod,
    startDate,
    endDate,
    availabilityCutoff,
  );

  if (!nominalRepresentation) {
    return null;
  }

  return `Time Period: ${nominalRepresentation}`;
}
